import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Header from './components/Header';
import Home from './pages/Home';
import NotFound from './components/NotFound';
import LoginForm from './components/login/LoginForm';
import Editor from './pages/editor';
import Dashboard from './pages/Dashboard';

// ─────────────────────────────────────────────────────────────────────────────
// Auth Utilities (can be extracted to a separate file later)
// ─────────────────────────────────────────────────────────────────────────────
const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { role: payload.role };
  } catch (err) {
    console.error('Token decode failed:', err);
    return null;
  }
};

const getInitialAuthState = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return { isAuthenticated: false, role: null };

  const decoded = decodeToken(token);
  if (!decoded) {
    localStorage.removeItem('access_token');
    return { isAuthenticated: false, role: null };
  }

  return {
    isAuthenticated: true,
    role: decoded.role,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Protected Route Component
// ─────────────────────────────────────────────────────────────────────────────
// const ProtectedRoute = ({ isAuthenticated, children }) => {
//   const location = useLocation();
//   return isAuthenticated ? (
//     children
//   ) : (
//     <Navigate to="/login" state={{ from: location }} replace />
//   );
// };

// ─────────────────────────────────────────────────────────────────────────────
// Login Wrapper
// ─────────────────────────────────────────────────────────────────────────────
const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleSuccess = useCallback(
    (token) => {
      onLoginSuccess(token);
      navigate(from, { replace: true });
    },
    [onLoginSuccess, navigate, from]
  );

  return <LoginForm onLogin={handleSuccess} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main App Component
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  const [auth, setAuth] = useState(() => getInitialAuthState());
  const [loading, setLoading] = useState(true);

  const isAuthenticated = auth.isAuthenticated;
  const isAdmin = auth.role === 'admin';

  const handleLogin = useCallback((token) => {
    localStorage.setItem('access_token', token);
    const decoded = decodeToken(token);

    if (decoded) {
      setAuth({
        isAuthenticated: true,
        role: decoded.role,
      });
    } else {
      localStorage.removeItem('access_token');
      setAuth({ isAuthenticated: false, role: null });
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('access_token');
    setAuth({ isAuthenticated: false, role: null });
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      {isAuthenticated ? (
        <>
          <Header isAuthenticated={true} isAdmin={isAdmin} handleLogout={handleLogout} />

          <Routes>
            <Route path="/" element={<Home />} />
            {/* <Route path="/editor" element={<Editor />} /> */}
            <Route path="/editor/:projectId" element={<Editor />} />
            <Route path="/dashboard" element={<Dashboard />} />


            {/* Add admin-only routes here if needed */}
            {/* <Route path="/admin" element={<AdminDashboard />} /> */}

            <Route path="*" element={<NotFound />} />
          </Routes>
        </>
      ) : (
        <Routes>
          <Route path="/login" element={<Login onLoginSuccess={handleLogin} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </div>
  );
}

export default App;
